import { useState } from 'react';
import { useDmPracticeSearch, type DmSearchState } from '../../../../../hooks/usePracticeDm';
import type { DmPracticeOption } from '../../../../../services/practiceDm';
import {
    SearchPickerField,
    SearchPickerList,
    type SearchPickerRow,
    type SearchPickerStatus
} from '../../../../shared/SearchPicker';
import { dmErrorMessage } from './dmErrorMessage';
import {
    MAX_DM_PRACTICES,
    addDmPractice,
    removeDmPractice,
    setMainDmPractice
} from './dmPracticeList.ts';
import { dmPracticePlace } from './dmPracticeText.ts';

interface DmPracticesPickerProps {
    practiceId: string;
    practices: DmPracticeOption[];
    onChange: (next: DmPracticeOption[]) => void;
    onCreateNew: () => void;
    readOnly: boolean;
}

function searchStatus(
    state: DmSearchState<DmPracticeOption>,
    matchCount: number
): SearchPickerStatus | null {
    if (!state.isActive) return { text: 'Type at least 2 characters.' };
    if (state.isSearching) return { text: 'Searching DM…' };
    if (state.error)
        return { text: dmErrorMessage(state.error, 'DM search failed.'), isError: true };
    return matchCount === 0 ? { text: 'No practices found.' } : null;
}

function MainBadge() {
    return (
        <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            Main
        </span>
    );
}

function SetMainButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="text-[11px] font-semibold text-gray-500 hover:text-gray-900"
        >
            Set as main
        </button>
    );
}

interface PracticeSearchProps {
    practiceId: string;
    practices: DmPracticeOption[];
    onAdd: (practice: DmPracticeOption) => void;
    onCreateNew: () => void;
}

function PracticeSearch({ practiceId, practices, onAdd, onCreateNew }: PracticeSearchProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const state = useDmPracticeSearch(practiceId, search);
    const takenIds = new Set(practices.map((p) => p.practiceId));
    const matches = state.results.filter((p) => !takenIds.has(p.practiceId));

    const close = () => {
        setOpen(false);
        setSearch('');
    };

    return (
        <SearchPickerField
            search={search}
            onSearchChange={setSearch}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={close}
            placeholder="Search DM practices by name or id"
            status={searchStatus(state, matches.length)}
            options={matches.map((p) => ({
                key: p.practiceId,
                title: p.name,
                subline: dmPracticePlace(p),
                meta: `#${p.practiceId}`,
                onPick: () => {
                    onAdd(p);
                    close();
                }
            }))}
            action={{
                label: 'Create new DM practice',
                onClick: () => {
                    close();
                    onCreateNew();
                }
            }}
        />
    );
}

function practiceRows(
    practices: DmPracticeOption[],
    readOnly: boolean,
    onChange: (next: DmPracticeOption[]) => void
): SearchPickerRow[] {
    return practices.map((p, index): SearchPickerRow => ({
        key: p.practiceId,
        title: p.name,
        subline: dmPracticePlace(p),
        meta: `#${p.practiceId}`,
        badge:
            index === 0 ? (
                <MainBadge />
            ) : (
                !readOnly && (
                    <SetMainButton
                        onClick={() => onChange(setMainDmPractice(practices, p.practiceId))}
                    />
                )
            ),
        onRemove: readOnly ? undefined : () => onChange(removeDmPractice(practices, p.practiceId)),
        removeLabel: `Remove ${p.name}`
    }));
}

export function DmPracticesPicker({
    practiceId,
    practices,
    onChange,
    onCreateNew,
    readOnly
}: DmPracticesPickerProps) {
    const rows = practiceRows(practices, readOnly, onChange);
    const atCap = practices.length >= MAX_DM_PRACTICES;

    return (
        <section className="flex flex-col gap-2">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Practices
                {practices.length > 0 && <> &middot; {practices.length}</>}
            </span>
            {!readOnly && (
                <p className="text-[12px] text-gray-500">
                    DM can't add practices to a doctor later, so add every practice this doctor
                    works at now.
                </p>
            )}
            {!readOnly && !atCap && (
                <PracticeSearch
                    practiceId={practiceId}
                    practices={practices}
                    onAdd={(practice) => onChange(addDmPractice(practices, practice))}
                    onCreateNew={onCreateNew}
                />
            )}
            {!readOnly && atCap && (
                <p className="text-[13px] text-gray-500">
                    That's the limit: a main practice plus {MAX_DM_PRACTICES - 1} others.
                </p>
            )}
            <SearchPickerList rows={rows} emptyText="No practice added yet." />
        </section>
    );
}
